import { useEffect, useRef, useState } from "react";
import type { EpisodeSummary, Extra, Me } from "@scrile/contracts";
import { useApi } from "../api/context";
import { Avatar } from "../components/Avatar";
import { EpisodeList } from "../components/EpisodeList";
import { EpisodesBlock } from "../components/EpisodesBlock";
import { Icon } from "../components/Icon";
import { Link } from "../components/Link";
import { Spinner } from "../components/Spinner";
import { t } from "../i18n";
import { followingEpisode, isSubscribed, resumeTarget, type EpisodeState } from "../lib/episodes";
import { formatSchedule } from "../lib/format";
import { trialDaysFor, type PlanId } from "../lib/money";
import { episodeKey, loadPosition } from "../lib/positions";
import { readItem, writeItem } from "../lib/safeStorage";
import { formatCountdown } from "../lib/sleepTimer";
import { href, navigate, type Route } from "../lib/router";
import { ExtrasList } from "../player/ExtrasList";
import { GiftSheet } from "../player/GiftSheet";
import { HeroCardPopup } from "../player/HeroCardPopup";
import { usePlayer, usePlayerActions, type PlayerState } from "../player/PlayerContext";
import { Scrubber } from "../player/Scrubber";
import { SleepSheet } from "../player/SleepSheet";
import { Transcript } from "../player/Transcript";
import { useHeroData } from "../state/heroData";
import { useToast } from "../state/toast";

type ListenRoute = Extract<Route, { name: "listen" }>;

const TRANSCRIPT_KEY = "scrile.transcript";
const CONFIRM_ATTEMPTS = 8;
const CONFIRM_DELAY_MS = 1_500;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Покупка видна в /me: «всё сразу» — по unlockedAll, подписка — по статусу. */
function confirmed(me: Me, plan: PlanId | null): boolean {
  if (plan === "all_at_once") return me.unlockedAll;
  if (plan) return isSubscribed(me);
  return isSubscribed(me) || me.unlockedAll;
}

function welcomeText(me: Me, plan: PlanId | null, trialDays: number): string {
  if (plan === "all_at_once" && me.unlockedAll) return t("player.welcome.unlocked");
  if (me.subscription?.status === "trialing") return t("player.welcome.trialing", { days: trialDays });
  if (isSubscribed(me)) return t("player.welcome.active");
  return t("player.welcome.unlocked");
}

/**
 * Шаблон «Плеер»: серия с расшифровкой и таймером сна, список сезона, рубрики и
 * голосовые, подарок герою. Адрес /listen/:n следует за тем, что играет, — после
 * перезагрузки или возврата из оплаты человек оказывается там же.
 */
export function Player({ route }: { route: ListenRoute }) {
  const api = useApi();
  const { hero, me, episodes, episodesReady, reloadMe, reloadEpisodes } = useHeroData();
  const player = usePlayer();
  const actions = usePlayerActions();
  const toast = useToast();
  const [extras, setExtras] = useState<Extra[] | null>(null);
  const [sleepOpen, setSleepOpen] = useState(false);
  const [giftOpen, setGiftOpen] = useState(false);
  const [showTranscript, setShowTranscript] = useState(() => readItem("local", TRANSCRIPT_KEY) !== "0");
  const [welcome, setWelcome] = useState<{ text: string; pending: boolean } | null>(null);
  const [confirming, setConfirming] = useState(route.checkout === "success");

  const mounted = useRef(false);
  const checkoutHandled = useRef(false);
  const routeEpisode = useRef<number | null | undefined>(undefined);
  const latest = useRef({ player, episodes });
  latest.current = { player, episodes };

  const finished = (ordinal: number) => loadPosition(episodeKey(hero.slug, ordinal))?.finished === true;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Вернулись из оплаты: вебхук Stripe мог ещё не дойти — несколько раз спрашиваем /me
  useEffect(() => {
    if (route.checkout !== "success" || checkoutHandled.current) return;
    checkoutHandled.current = true;
    const plan = route.plan;
    navigate(href.listen(route.episode), { replace: true });
    void (async () => {
      setWelcome({ text: t("player.welcome.pending"), pending: true });
      let paid: Me | null = null;
      for (let attempt = 0; attempt < CONFIRM_ATTEMPTS && mounted.current; attempt += 1) {
        const fresh = await reloadMe().catch(() => null);
        if (fresh && confirmed(fresh, plan)) {
          paid = fresh;
          break;
        }
        await wait(CONFIRM_DELAY_MS);
      }
      if (!mounted.current) return;
      const list = await reloadEpisodes().catch(() => latest.current.episodes);
      setWelcome({ text: paid ? welcomeText(paid, plan, hero.pricing.trialDays) : t("player.welcome.late"), pending: false });
      setConfirming(false);
      // Пилот дослушан — сразу предлагаем следующую серию, а не снова пилот
      const current = latest.current.player;
      const target = resumeTarget(list, finished);
      if (target && (current.track === null || current.status === "ended" || current.status === "idle")) {
        void actions.playEpisode(target.ordinal, { autoplay: false });
      }
    })();
  }, [route.checkout]);

  // Вернулись из оплаты подарка: спасибо и чистый адрес
  useEffect(() => {
    if (route.gift !== "thanks") return;
    toast.show(t("gift.thanks", { creator: hero.creator.name }), 6_000);
    navigate(href.listen(route.episode), { replace: true });
  }, [route.gift, route.episode, toast, hero.creator.name]);

  // Адрес → плеер: переход по ссылке или «назад» выбирает серию
  useEffect(() => {
    if (!episodesReady || route.episode === routeEpisode.current) return;
    routeEpisode.current = route.episode;
    if (route.episode !== null && actions.requestedOrdinal() !== route.episode) {
      void actions.playEpisode(route.episode, { autoplay: false });
    }
  }, [route.episode, episodesReady, actions]);

  // /listen без номера: если ничего не играет — предложить, с чего продолжить
  useEffect(() => {
    if (route.episode !== null || confirming || !episodesReady) return;
    if (latest.current.player.track !== null || actions.requestedOrdinal() !== null) return;
    const target = resumeTarget(episodes, finished);
    if (target) void actions.playEpisode(target.ordinal, { autoplay: false });
  }, [route.episode, confirming, episodesReady, episodes]);

  // Плеер → адрес: автопереход и выбор в списке переписывают /listen/:n без новой записи
  // истории. Серия — из ref, а не из рендера: при «назад» эффект выше в этом же проходе уже
  // попросил новую серию, а player.requested ещё старый — и адрес вернулся бы назад
  useEffect(() => {
    const requested = actions.requestedOrdinal();
    if (requested === null || requested === route.episode) return;
    routeEpisode.current = requested;
    navigate(href.listen(requested), { replace: true });
  }, [player.requested, route.episode, actions]);

  // Рубрики и голосовые — заново, когда меняется подписка
  useEffect(() => {
    let cancelled = false;
    api
      .extras()
      .then((list) => !cancelled && setExtras(list))
      .catch(() => !cancelled && setExtras([]));
    return () => {
      cancelled = true;
    };
  }, [api, me?.id, me?.subscription?.status]);

  function toggleTranscript() {
    const next = !showTranscript;
    setShowTranscript(next);
    writeItem("local", TRANSCRIPT_KEY, next ? "1" : "0");
  }

  function onSelect(episode: EpisodeSummary, state: EpisodeState) {
    if (state === "locked") navigate(href.plans({ episode: episode.ordinal }));
    else if (state === "free" || state === "available") void actions.playEpisode(episode.ordinal);
  }

  function openGift() {
    if (!me) {
      navigate(href.join(window.location.pathname));
      return;
    }
    setGiftOpen(true);
  }

  const track = player.track;
  const episodeTrack = track?.kind === "episode" ? track : null;
  const playing = player.status === "playing";
  const loading = player.status === "loading" || (player.buffering && player.status !== "paused");
  const subscribed = isSubscribed(me);
  const trialDays = trialDaysFor(hero.pricing, me ? me.trialEligible : null);
  const season = hero.currentSeason;
  const next = episodeTrack ? followingEpisode(episodes, episodeTrack.ordinal) : null;
  const nextOut = next?.scheduledAt ? { n: next.ordinal, date: formatSchedule(next.scheduledAt, me?.timezone) } : null;

  return (
    <main className="container player-page">
      {welcome && (
        <div className={`notice${welcome.pending ? "" : " notice--success"}`} role="status">
          {welcome.pending ? <Spinner size={18} /> : <Icon name="sparkle" size={18} />}
          <span>{welcome.text}</span>
          {!welcome.pending && (
            <button type="button" className="icon-btn icon-btn--quiet icon-btn--sm" onClick={() => setWelcome(null)} aria-label={t("app.close")}>
              <Icon name="close" size={16} />
            </button>
          )}
        </div>
      )}

      <div className="player-layout">
        <div className="player-main">
          <section className="panel now-playing" aria-label={track?.title ?? t("player.loading")}>
            <div className="now-playing__head">
              <Avatar src={hero.theme.portraitUrl ?? hero.theme.coverUrl} name={hero.name} size={76} className="now-playing__art" />
              <div className="now-playing__titles">
                <p className="eyebrow">
                  {episodeTrack
                    ? t("episode.number", { n: episodeTrack.ordinal })
                    : track?.kind === "extra"
                      ? t(track.extra.kind === "rubric" ? "player.extra.rubric" : "player.extra.voice_note")
                      : track?.kind === "trailer"
                        ? t("player.trailer")
                        : hero.name}
                </p>
                <h1 className="now-playing__title">{track?.title ?? (player.status === "loading" ? t("player.loading") : hero.name)}</h1>
              </div>
            </div>

            <Scrubber currentTime={player.currentTime} duration={player.duration} onSeek={(seconds) => actions.seek(seconds)} disabled={!track} />

            <div className="controls">
              <button type="button" className="icon-btn icon-btn--lg" onClick={() => actions.skip(-15)} disabled={!track} aria-label={t("player.back15")}>
                <Icon name="back15" size={30} />
              </button>
              <button
                type="button"
                className="play-btn"
                onClick={() => actions.toggle()}
                disabled={!track}
                aria-label={playing ? t("player.pause") : t("player.play")}
              >
                {loading && !playing ? <Spinner size={30} /> : <Icon name={playing ? "pause" : "play"} size={32} />}
              </button>
              <button type="button" className="icon-btn icon-btn--lg" onClick={() => actions.skip(15)} disabled={!track} aria-label={t("player.forward15")}>
                <Icon name="forward15" size={30} />
              </button>
            </div>

            <div className="pills">
              <button type="button" className="pill" onClick={() => actions.cycleRate()} aria-label={t("player.speed", { rate: player.rate })}>
                {player.rate}×
              </button>
              <button
                type="button"
                className={`pill${player.sleep.mode !== "off" ? " pill--active" : ""}`}
                onClick={() => setSleepOpen(true)}
                aria-label={t("sleep.title")}
              >
                <Icon name="moon" size={18} />
                <span>
                  {player.sleep.mode === "timer" && player.sleepRemainingMs !== null
                    ? formatCountdown(player.sleepRemainingMs)
                    : player.sleep.mode === "episode_end"
                      ? t("sleep.endsAtEpisodeEnd")
                      : t("sleep.button")}
                </span>
              </button>
              {episodeTrack && (
                <button
                  type="button"
                  className={`pill${showTranscript ? " pill--active" : ""}`}
                  onClick={toggleTranscript}
                  aria-pressed={showTranscript}
                  aria-label={showTranscript ? t("player.transcriptHide") : t("player.transcriptShow")}
                >
                  <Icon name="transcript" size={18} />
                  <span>{t("player.transcript")}</span>
                </button>
              )}
            </div>

            {player.error && (
              <p className="form__error" role="alert">
                {player.error}
              </p>
            )}
            <EndNote player={player} subscribed={subscribed} trialDays={trialDays} nextOut={nextOut} />
          </section>

          {episodeTrack && showTranscript && (
            <section className="panel transcript-panel" aria-label={t("player.transcript")}>
              <h2 className="panel__title">{t("player.transcript")}</h2>
              <Transcript lines={episodeTrack.manifest.transcript} currentTime={player.currentTime} onSeek={(seconds) => actions.seek(seconds)} />
              <Mentioned cards={episodeTrack.manifest.heroCards} currentTime={player.currentTime} />
            </section>
          )}
        </div>

        <aside className="player-side">
          <section className="panel" aria-labelledby="season-title">
            <h2 className="panel__title" id="season-title">
              {season ? `${t("hero.season", { n: season.ordinal })} · ${season.title}` : t("player.season")}
            </h2>
            <EpisodesBlock>
              <EpisodeList
                episodes={episodes}
                heroSlug={hero.slug}
                timeZone={me?.timezone}
                currentOrdinal={episodeTrack?.ordinal ?? null}
                playing={playing}
                onSelect={onSelect}
              />
            </EpisodesBlock>
          </section>

          <section className="panel" aria-labelledby="extras-title">
            <h2 className="panel__title" id="extras-title">
              {t("player.extras")}
            </h2>
            {extras === null ? (
              <Spinner />
            ) : extras.length > 0 ? (
              <>
                {extras.some((extra) => extra.locked) && <p className="hint extras-hint">{t("player.extras.locked")}</p>}
                <ExtrasList
                  extras={extras}
                  currentId={track?.kind === "extra" ? track.id : null}
                  playing={playing}
                  onPlay={(extra) => actions.playExtra(extra)}
                  onLocked={() => navigate(href.plans())}
                />
              </>
            ) : (
              <p className="muted">{t("player.extras.empty")}</p>
            )}
          </section>

          <section className="gift-row">
            <button type="button" className="btn btn--outline btn--block" onClick={openGift}>
              <Icon name="gift" size={18} />
              {t("player.gift")}
            </button>
            <p className="hint center">{t("player.giftNote", { creator: hero.creator.name })}</p>
          </section>
        </aside>
      </div>

      {episodeTrack && (
        <HeroCardPopup cards={episodeTrack.manifest.heroCards} currentTime={player.currentTime} trackKey={episodeTrack.key} />
      )}
      {sleepOpen && <SleepSheet sleep={player.sleep} onChoose={(option) => actions.setSleep(option)} onClose={() => setSleepOpen(false)} />}
      {giftOpen && <GiftSheet onClose={() => setGiftOpen(false)} />}
    </main>
  );
}

/** Под плеером — что дальше, когда серия кончилась и следующей нет. */
function EndNote({
  player,
  subscribed,
  trialDays,
  nextOut,
}: {
  player: PlayerState;
  subscribed: boolean;
  trialDays: number;
  /** Когда выйдет следующая серия — по часам подписчика (EpisodeSummary.scheduledAt). */
  nextOut: { n: number; date: string } | null;
}) {
  if (player.status !== "ended") return null;
  if (player.endedReason === "caught_up") {
    return (
      <div className="end-note">
        <strong>{t("player.caughtUp.title")}</strong>
        <span className="muted">{nextOut ? t("player.caughtUp.next", nextOut) : t("player.caughtUp.body")}</span>
      </div>
    );
  }
  if (player.endedReason === "paywall" && !subscribed && player.track?.kind === "episode") {
    const next = player.track.ordinal + 1;
    return (
      <div className="end-note">
        <strong>{t("player.pilotEnd.title")}</strong>
        <span className="muted">{t("player.pilotEnd.body", { n: next })}</span>
        <Link to={href.plans({ episode: next })} className="btn btn--primary btn--sm">
          {trialDays > 0 ? t("paywall.cta.trial", { days: trialDays }) : t("player.pilotEnd.cta")}
        </Link>
      </div>
    );
  }
  return null;
}

/** Упомянутые герои — после своего момента, чтобы не раскрывать сюжет заранее. */
function Mentioned({ cards, currentTime }: { cards: readonly { atSec: number; slug: string; name: string; url: string }[]; currentTime: number }) {
  const seen = cards.filter((card) => currentTime >= card.atSec);
  if (seen.length === 0) return null;
  return (
    <div className="mentioned">
      <span className="muted">{t("player.mentioned")}</span>
      {seen.map((card) => (
        <a key={`${card.slug}@${card.atSec}`} className="chip chip--link" href={card.url} target="_blank" rel="noopener">
          {card.name}
          <Icon name="external" size={14} />
        </a>
      ))}
    </div>
  );
}
